import React, { useState, useEffect } from 'react';
import { BookOpen, Calculator, Map, Users, TrendingUp, AlertTriangle, CheckCircle, Play, Pause, RotateCcw, Target, Navigation, MapPin, Anchor, Layers, DollarSign, Settings } from 'lucide-react';
import { BarChart, Bar, PieChart, Pie, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, LineChart, Line, Cell, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ComposedChart, Area } from 'recharts';
import { MapContainer, TileLayer, Marker, Popup, Polyline, CircleMarker, Tooltip as LeafletTooltip } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix pour les icônes Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const LogisticsLearningApp = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [simulationStep, setSimulationStep] = useState(0);
  const [isSimulationRunning, setIsSimulationRunning] = useState(false);
  const [userChoices, setUserChoices] = useState({});
  const [score, setScore] = useState(0);
  const [kpiHistory, setKpiHistory] = useState([]);
  
  // États pour l'optimisation transport
  const [selectedPort, setSelectedPort] = useState('mixed');
  const [optimizationResults, setOptimizationResults] = useState(null);
  const [showRoutes, setShowRoutes] = useState(true);
  
  // Nouveaux états pour les améliorations
  const [weights, setWeights] = useState({
    cost: 40,
    delay: 30,
    risk: 20,
    co2: 10
  });
  const [mapLayers, setMapLayers] = useState({
    roads: true,
    climate: false,
    refuel: false,
    cities: true
  });
  const [showCostDetails, setShowCostDetails] = useState(false);
  const [showAdvancedConfig, setShowAdvancedConfig] = useState(false);

  const tabs = [
    { id: 'overview', label: 'Vue d\'ensemble', icon: BookOpen },
    { id: 'calculator', label: 'Calculateur', icon: Calculator },
    { id: 'simulation', label: 'Simulation', icon: Play },
    { id: 'scenarios', label: 'Scénarios', icon: Target },
    { id: 'transport', label: 'Optimisation Logistique', icon: Navigation }
  ];

  // Données géographiques enrichies
  const ports = {
    nouakchott: {
      name: "Port de Nouakchott",
      coords: [18.0945, -15.9582],
      capacity: 40,
      tirantEau: 10,
      cost: 15,
      storageCost: 0.5,
      handlingCost: 8,
      color: '#3b82f6'
    },
    nouadhibou: {
      name: "Port de Nouadhibou",
      coords: [20.9000, -17.0333],
      capacity: 100,
      tirantEau: 15,
      cost: 18,
      storageCost: 0.4,
      handlingCost: 12,
      color: '#10b981'
    }
  };

  const projectSite = {
    name: "Projet RiaH2",
    coords: [20.12411, -15.63087],
    description: "20 GW (12 GW éolien + 8 GW solaire) + 10 GW électrolyse"
  };

  // Routes enrichies
  const routes = {
    nouakchott_to_riah2: {
      path: [
        [18.0945, -15.9582],
        [19.0000, -15.8000],
        [20.12411, -15.63087]
      ],
      distance: 230,
      roadType: "asphalté",
      cost: 2.5,
      avgSpeed: 60,
      risk: 30,
      co2PerKm: 0.12
    },
    nouadhibou_to_riah2: {
      path: [
        [20.9000, -17.0333],
        [20.5000, -16.5000],
        [20.12411, -15.63087]
      ],
      distance: 180,
      roadType: "mixte",
      cost: 3.2,
      avgSpeed: 45,
      risk: 45,
      co2PerKm: 0.15
    }
  };

  // Données de couches supplémentaires
  const cities = [
    { name: "Nouakchott", coords: [18.0735, -15.9582], type: "capitale" },
    { name: "Nouadhibou", coords: [20.9000, -17.0333], type: "ville" },
    { name: "Atar", coords: [20.5167, -13.0500], type: "ville" },
    { name: "Boutilimit", coords: [17.5500, -14.7000], type: "ville" }
  ];

  const refuelPoints = [
    { name: "Station Boutilimit", coords: [17.5500, -14.7000] },
    { name: "Station Atar", coords: [20.5167, -13.0500] },
    { name: "Aire RN3", coords: [17.8000, -14.2000] }
  ];

  const climateZones = [
    { name: "Zone tempêtes fréquentes", coords: [19.5000, -14.0000], radius: 80000, risk: "élevé", color: "#ef4444" },
    { name: "Zone chaleur extrême", coords: [18.0000, -12.5000], radius: 60000, risk: "moyen", color: "#f59e0b" },
    { name: "Banc d'Arguin (Zone protégée)", coords: [20.2000, -16.4000], radius: 50000, risk: "environnemental", color: "#16a34a" },
    { name: "Parc National du Diawling (Zone protégée)", coords: [16.3500, -16.3000], radius: 40000, risk: "environnemental", color: "#16a34a" }
  ];

  const roadNetwork = [
    { name: "RN1", path: [[18.0945, -15.9582], [20.9000, -17.0333]], type: "nationale" },
    { name: "RN2", path: [[20.9000, -17.0333], [20.5167, -13.0500]], type: "nationale" },
    { name: "RN3", path: [[18.0945, -15.9582], [19.0000, -15.8000], [20.12411, -15.63087]], type: "nationale" }
  ];

  const projectComponents = {
    eoliennes: {
      name: "Éoliennes 6 MW",
      quantity: 2000,
      unitWeight: 240,
      totalWeight: 480000,
      dimensions: { length: 75, width: 5, height: 5 }
    },
    panneaux: {
      name: "Panneaux Solaires",
      quantity: 13333333,
      unitWeight: 0.0375,
      totalWeight: 500000,
      dimensions: { length: 2, width: 1, height: 0.05 }
    },
    electrolyseurs: {
      name: "Électrolyseurs 10 MW",
      quantity: 1000,
      unitWeight: 80,
      totalWeight: 80000,
      dimensions: { length: 12, width: 3, height: 4 }
    }
  };

  const simulationData = [
    {
      phase: "Phase 1 - Préparation & Éolien",
      duration: "Mois 1-24",
      challenges: ["Développement portuaire", "Formation équipes", "Transport éoliennes lourdes"],
      kpis: { cost: 800, efficiency: 60, risk: 30 },
      components: "400 éoliennes installées"
    },
    {
      phase: "Phase 2 - Solaire & Montée H2", 
      duration: "Mois 25-60",
      challenges: ["Coordination multi-flux", "Transport panneaux masse", "Premiers électrolyseurs"],
      kpis: { cost: 2200, efficiency: 75, risk: 40 },
      components: "8 GW solaire + 300 modules H2"
    },
    {
      phase: "Phase 3 - Finalisation H2",
      duration: "Mois 61-96", 
      challenges: ["Optimisation 3 technologies", "Synchronisation production", "Qualité H2"],
      kpis: { cost: 4200, efficiency: 90, risk: 25 },
      components: "700 modules H2 restants"
    },
    {
      phase: "Phase 4 - Pleine capacité",
      duration: "Mois 97-120", 
      challenges: ["Maintenance préventive", "Export H2", "Optimisation énergétique"],
      kpis: { cost: 5200, efficiency: 95, risk: 20 },
      components: "12GW + 10GW H2 opérationnels"
    }
  ];

  const scenarios = [
    {
      id: 1,
      title: "Congestion portuaire multi-flux",
      description: "Nouakchott surchargé: éoliennes, panneaux et électrolyseurs arrivent simultanément",
      choices: [
        { text: "Répartition forcée sur Nouadhibou", impact: { cost: +80, delay: +1, risk: +15 } },
        { text: "Stockage temporaire offshore", impact: { cost: +150, delay: +0.5, risk: +5 } },
        { text: "Décalage planning livraisons", impact: { cost: +20, delay: +3, risk: -5 } }
      ]
    },
    {
      id: 2,
      title: "Défaillance module électrolyse 10MW",
      description: "Module H2 endommagé en transport, retard installation 200MW",
      choices: [
        { text: "Réparation en Europe + réexpédition", impact: { cost: +40, delay: +8, risk: +10 } },
        { text: "Module de secours du fournisseur", impact: { cost: +120, delay: +2, risk: +5 } },
        { text: "Reconfiguration avec modules 5MW", impact: { cost: +60, delay: +4, risk: +15 } }
      ]
    },
    {
      id: 3,
      title: "Rupture approvisionnement panneaux",
      description: "Usine chinoise fermée COVID, 5M panneaux bloqués",
      choices: [
        { text: "Fournisseur alternatif (prix +20%)", impact: { cost: +200, delay: +1, risk: +5 } },
        { text: "Attendre réouverture usine", impact: { cost: +10, delay: +6, risk: +20 } },
        { text: "Mix fournisseurs européens", impact: { cost: +300, delay: +2, risk: -10 } }
      ]
    }
  ];

  useEffect(() => {
    if (isSimulationRunning) {
      const interval = setInterval(() => {
        setSimulationStep(prev => {
          if (prev >= simulationData.length - 1) {
            setIsSimulationRunning(false);
            return prev;
          }
          return prev + 1;
        });
      }, 4000);
      return () => clearInterval(interval);
    }
  }, [isSimulationRunning]);

  useEffect(() => {
    if (isSimulationRunning && simulationStep < simulationData.length) {
      setKpiHistory(prev => [...prev, {
        step: simulationStep,
        ...simulationData[simulationStep].kpis
      }]);
    }
  }, [simulationStep, isSimulationRunning]);

  const [currentScenario, setCurrentScenario] = useState(null);
  const [scenarioResult, setScenarioResult] = useState(null);

  // Fonction d'optimisation améliorée avec pondération
  const calculateOptimization = (strategy, customWeights = weights) => {
    let allocation = {};
    let costDetails = {};
    let totalCost = 0;
    let avgDelay = 0;
    let totalRisk = 0;
    let totalCO2 = 0;

    const normalizedWeights = {
      cost: customWeights.cost / 100,
      delay: customWeights.delay / 100,
      risk: customWeights.risk / 100,
      co2: customWeights.co2 / 100
    };

    switch(strategy) {
      case 'nouakchott':
        allocation = {
          eoliennes: { nouakchott: 100, nouadhibou: 0 },
          panneaux: { nouakchott: 100, nouadhibou: 0 },
          electrolyseurs: { nouakchott: 100, nouadhibou: 0 }
        };
        
        costDetails = {
          portCosts: {
            unloading: 120,
            storage: 15,
            handling: 45,
            total: 180
          },
          transportCosts: {
            standard: 35,
            exceptional: 20,
            escort: 5,
            insurance: 5,
            total: 65
          },
          indirectCosts: {
            capitalImmobilization: 0,
            damageProvision: 0,
            administrative: 0,
            total: 0
          },
          total: 245
        };
        
        totalCost = 245;
        avgDelay = 18;
        totalRisk = 65;
        totalCO2 = 45;
        break;

      case 'nouadhibou':
        allocation = {
          eoliennes: { nouakchott: 0, nouadhibou: 100 },
          panneaux: { nouakchott: 0, nouadhibou: 100 },
          electrolyseurs: { nouakchott: 0, nouadhibou: 100 }
        };
        
        costDetails = {
          portCosts: {
            unloading: 135,
            storage: 12,
            handling: 55,
            total: 202
          },
          transportCosts: {
            standard: 42,
            exceptional: 18,
            escort: 4,
            insurance: 2,
            total: 66
          },
          indirectCosts: {
            capitalImmobilization: 0,
            damageProvision: 0,
            administrative: 0,
            total: 0
          },
          total: 268
        };
        
        totalCost = 268;
        avgDelay = 22;
        totalRisk = 48;
        totalCO2 = 52;
        break;

      case 'mixed':
        allocation = {
          eoliennes: { nouakchott: 40, nouadhibou: 60 },
          panneaux: { nouakchott: 70, nouadhibou: 30 },
          electrolyseurs: { nouakchott: 0, nouadhibou: 100 }
        };
        
        costDetails = {
          portCosts: {
            unloading: 105,
            storage: 10,
            handling: 42,
            total: 157
          },
          transportCosts: {
            standard: 38,
            exceptional: 22,
            escort: 6,
            insurance: 4,
            total: 70
          },
          indirectCosts: {
            capitalImmobilization: 0.5,
            damageProvision: 0.3,
            administrative: 0.2,
            total: 1
          },
          total: 228
        };
        
        totalCost = 228;
        avgDelay = 16;
        totalRisk = 52;
        totalCO2 = 42;
        break;

      default:
        allocation = {
          eoliennes: { nouakchott: 40, nouadhibou: 60 },
          panneaux: { nouakchott: 70, nouadhibou: 30 },
          electrolyseurs: { nouakchott: 0, nouadhibou: 100 }
        };
        totalCost = 228;
        avgDelay = 16;
        totalRisk = 52;
        totalCO2 = 42;
    }

    // Calcul du score pondéré
    const normalizedCost = (300 - totalCost) / 300;
    const normalizedDelay = (30 - avgDelay) / 30;
    const normalizedRisk = (100 - totalRisk) / 100;
    const normalizedCO2 = (60 - totalCO2) / 60;

    const weightedScore = (
      normalizedCost * normalizedWeights.cost +
      normalizedDelay * normalizedWeights.delay +
      normalizedRisk * normalizedWeights.risk +
      normalizedCO2 * normalizedWeights.co2
    ) * 100;

    return {
      allocation,
      costDetails,
      totalCost,
      avgDelay,
      totalRisk,
      totalCO2,
      weightedScore: weightedScore.toFixed(1)
    };
  };

  useEffect(() => {
    const results = calculateOptimization(selectedPort, weights);
    setOptimizationResults(results);
  }, [selectedPort, weights]);

  // Composant pour l'onglet Optimisation Transport avec améliorations
  const TransportOptimizationTab = () => {
    const comparisonData = [
      {
        scenario: 'Nouakchott',
        ...calculateOptimization('nouakchott', weights)
      },
      {
        scenario: 'Nouadhibou',
        ...calculateOptimization('nouadhibou', weights)
      },
      {
        scenario: 'Mixte',
        ...calculateOptimization('mixed', weights)
      }
    ];

    // Données pour le graphique de décomposition des coûts
    const costBreakdownData = optimizationResults?.costDetails ? [
      { category: 'Déchargement', value: optimizationResults.costDetails.portCosts.unloading },
      { category: 'Stockage', value: optimizationResults.costDetails.portCosts.storage },
      { category: 'Manutention', value: optimizationResults.costDetails.portCosts.handling },
      { category: 'Transport Standard', value: optimizationResults.costDetails.transportCosts.standard },
      { category: 'Convois Exceptionnels', value: optimizationResults.costDetails.transportCosts.exceptional },
      { category: 'Escorte', value: optimizationResults.costDetails.transportCosts.escort },
      { category: 'Assurance', value: optimizationResults.costDetails.transportCosts.insurance }
    ] : [];

    return (
      <div className="space-y-6">
        {/* En-tête */}
        <div className="bg-gradient-to-r from-blue-600 to-green-600 text-white p-6 rounded-lg shadow-lg">
          <h2 className="text-3xl font-bold mb-2">Optimisation Transport Multi-Ports</h2>
          <p className="text-blue-100">
            Projet RiaH2 : 20 GW (12 GW éolien + 8 GW solaire) + 10 GW électrolyse
          </p>
        </div>

        {/* Panneau de Configuration Avancé */}
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-gray-800 flex items-center">
              <Settings className="mr-2 text-blue-600" />
              Configuration Avancée
            </h3>
            <button
              onClick={() => setShowAdvancedConfig(!showAdvancedConfig)}
              className="text-blue-600 hover:text-blue-800 text-sm font-medium"
            >
              {showAdvancedConfig ? 'Masquer' : 'Afficher'}
            </button>
          </div>
          
          {showAdvancedConfig && (
            <div className="space-y-6 border-t pt-4">
              <div>
                <h4 className="font-semibold text-gray-700 mb-4">Pondération des Critères d'Optimisation</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <div className="flex justify-between mb-2">
                      <label className="text-sm font-medium text-gray-700">Coût</label>
                      <span className="text-sm font-bold text-blue-600">{weights.cost}%</span>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={weights.cost}
                      onChange={(e) => setWeights({...weights, cost: parseInt(e.target.value)})}
                      className="w-full h-2 bg-blue-200 rounded-lg appearance-none cursor-pointer"
                    />
                  </div>

                  <div>
                    <div className="flex justify-between mb-2">
                      <label className="text-sm font-medium text-gray-700">Délai</label>
                      <span className="text-sm font-bold text-green-600">{weights.delay}%</span>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={weights.delay}
                      onChange={(e) => setWeights({...weights, delay: parseInt(e.target.value)})}
                      className="w-full h-2 bg-green-200 rounded-lg appearance-none cursor-pointer"
                    />
                  </div>

                  <div>
                    <div className="flex justify-between mb-2">
                      <label className="text-sm font-medium text-gray-700">Risque</label>
                      <span className="text-sm font-bold text-yellow-600">{weights.risk}%</span>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={weights.risk}
                      onChange={(e) => setWeights({...weights, risk: parseInt(e.target.value)})}
                      className="w-full h-2 bg-yellow-200 rounded-lg appearance-none cursor-pointer"
                    />
                  </div>

                  <div>
                    <div className="flex justify-between mb-2">
                      <label className="text-sm font-medium text-gray-700">Impact CO₂</label>
                      <span className="text-sm font-bold text-purple-600">{weights.co2}%</span>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={weights.co2}
                      onChange={(e) => setWeights({...weights, co2: parseInt(e.target.value)})}
                      className="w-full h-2 bg-purple-200 rounded-lg appearance-none cursor-pointer"
                    />
                  </div>
                </div>

                <div className="mt-4 flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                  <span className="text-sm text-gray-600">Total pondération:</span>
                  <span className={`text-lg font-bold ${weights.cost + weights.delay + weights.risk + weights.co2 === 100 ? 'text-green-600' : 'text-red-600'}`}>
                    {weights.cost + weights.delay + weights.risk + weights.co2}%
                  </span>
                </div>

                <div className="mt-3 flex space-x-2">
                  <button
                    onClick={() => setWeights({ cost: 40, delay: 30, risk: 20, co2: 10 })}
                    className="px-3 py-1 bg-blue-100 text-blue-700 rounded text-sm hover:bg-blue-200"
                  >
                    Profil Équilibré
                  </button>
                  <button
                    onClick={() => setWeights({ cost: 70, delay: 15, risk: 10, co2: 5 })}
                    className="px-3 py-1 bg-green-100 text-green-700 rounded text-sm hover:bg-green-200"
                  >
                    Priorité Coût
                  </button>
                  <button
                    onClick={() => setWeights({ cost: 15, delay: 70, risk: 10, co2: 5 })}
                    className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded text-sm hover:bg-yellow-200"
                  >
                    Priorité Délai
                  </button>
                  <button
                    onClick={() => setWeights({ cost: 20, delay: 20, risk: 50, co2: 10 })}
                    className="px-3 py-1 bg-red-100 text-red-700 rounded text-sm hover:bg-red-200"
                  >
                    Priorité Sécurité
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Stratégie Portuaire
              </label>
              <select
                value={selectedPort}
                onChange={(e) => setSelectedPort(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="nouakchott">Port Unique - Nouakchott</option>
                <option value="nouadhibou">Port Unique - Nouadhibou</option>
                <option value="mixed">Stratégie Mixte Optimisée (Recommandé)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Couches de la Carte
              </label>
              <div className="flex items-center space-x-4">
                <label className="flex items-center text-sm">
                  <input
                    type="checkbox"
                    checked={showRoutes}
                    onChange={(e) => setShowRoutes(e.target.checked)}
                    className="mr-2"
                  />
                  Routes
                </label>
                <label className="flex items-center text-sm">
                  <input
                    type="checkbox"
                    checked={mapLayers.roads}
                    onChange={(e) => setMapLayers({...mapLayers, roads: e.target.checked})}
                    className="mr-2"
                  />
                  Réseau routier
                </label>
                <label className="flex items-center text-sm">
                  <input
                    type="checkbox"
                    checked={mapLayers.climate}
                    onChange={(e) => setMapLayers({...mapLayers, climate: e.target.checked})}
                    className="mr-2"
                  />
                  Zones climatiques
                </label>
              </div>
              <div className="flex items-center space-x-4 mt-2">
                <label className="flex items-center text-sm">
                  <input
                    type="checkbox"
                    checked={mapLayers.refuel}
                    onChange={(e) => setMapLayers({...mapLayers, refuel: e.target.checked})}
                    className="mr-2"
                  />
                  Ravitaillement
                </label>
                <label className="flex items-center text-sm">
                  <input
                    type="checkbox"
                    checked={mapLayers.cities}
                    onChange={(e) => setMapLayers({...mapLayers, cities: e.target.checked})}
                    className="mr-2"
                  />
                  Villes
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Carte Interactive Enrichie */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
            <Layers className="mr-2 text-blue-600" />
            Carte Interactive - Infrastructure Logistique Mauritanie
          </h3>
          
          <div className="h-96 rounded-lg overflow-hidden border-2 border-gray-200">
            <MapContainer
              center={[19.0, -15.0]}
              zoom={6}
              style={{ height: '100%', width: '100%' }}
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              />
              
              {/* Ports */}
              <Marker position={ports.nouakchott.coords}>
                <Popup>
                  <div className="p-2">
                    <h4 className="font-bold text-blue-600">{ports.nouakchott.name}</h4>
                    <p className="text-sm">Capacité levage: {ports.nouakchott.capacity}t</p>
                    <p className="text-sm">Tirant d'eau: {ports.nouakchott.tirantEau}m</p>
                    <p className="text-sm">Coût déchargement: {ports.nouakchott.cost}€/t</p>
                    <p className="text-sm">Coût stockage: {ports.nouakchott.storageCost}€/m²/jour</p>
                  </div>
                </Popup>
              </Marker>
              
              <Marker position={ports.nouadhibou.coords}>
                <Popup>
                  <div className="p-2">
                    <h4 className="font-bold text-green-600">{ports.nouadhibou.name}</h4>
                    <p className="text-sm">Capacité levage: {ports.nouadhibou.capacity}t</p>
                    <p className="text-sm">Tirant d'eau: {ports.nouadhibou.tirantEau}m</p>
                    <p className="text-sm">Coût déchargement: {ports.nouadhibou.cost}€/t</p>
                    <p className="text-sm">Coût stockage: {ports.nouadhibou.storageCost}€/m²/jour</p>
                  </div>
                </Popup>
              </Marker>
              
              {/* Site du projet */}
              <Marker position={projectSite.coords}>
                <Popup>
                  <div className="p-2">
                    <h4 className="font-bold text-purple-600">{projectSite.name}</h4>
                    <p className="text-sm">{projectSite.description}</p>
                  </div>
                </Popup>
              </Marker>
              
              {/* Villes */}
              {mapLayers.cities && cities.map((city, idx) => (
                <CircleMarker
                  key={`city-${idx}`}
                  center={city.coords}
                  radius={city.type === 'capitale' ? 8 : 5}
                  fillColor={city.type === 'capitale' ? '#ef4444' : '#f59e0b'}
                  color="#fff"
                  weight={1}
                  opacity={1}
                  fillOpacity={0.7}
                >
                  <LeafletTooltip>{city.name}</LeafletTooltip>
                </CircleMarker>
              ))}
              
              {/* Points de ravitaillement */}
              {mapLayers.refuel && refuelPoints.map((point, idx) => (
                <CircleMarker
                  key={`refuel-${idx}`}
                  center={point.coords}
                  radius={4}
                  fillColor="#10b981"
                  color="#fff"
                  weight={1}
                  opacity={1}
                  fillOpacity={0.8}
                >
                  <LeafletTooltip>{point.name}</LeafletTooltip>
                </CircleMarker>
              ))}
              
              {/* Zones climatiques */}
              {mapLayers.climate && climateZones.map((zone, idx) => (
                <CircleMarker
                  key={`climate-${idx}`}
                  center={zone.coords}
                  radius={zone.radius / 5000}
                  fillColor={zone.color || (zone.risk === 'élevé' ? '#ef4444' : '#f59e0b')}
                  color={zone.color || (zone.risk === 'élevé' ? '#ef4444' : '#f59e0b')}
                  weight={2}
                  opacity={0.3}
                  fillOpacity={0.1}
                >
                  <LeafletTooltip>{zone.name} - {zone.risk === 'environnemental' ? 'Zone protégée' : `Risque ${zone.risk}`}</LeafletTooltip>
                </CircleMarker>
              ))}
              
              {/* Réseau routier */}
              {mapLayers.roads && roadNetwork.map((road, idx) => (
                <Polyline
                  key={`road-${idx}`}
                  positions={road.path}
                  color="#94a3b8"
                  weight={2}
                  opacity={0.5}
                  dashArray="5, 10"
                >
                  <LeafletTooltip>{road.name} - Route {road.type}</LeafletTooltip>
                </Polyline>
              ))}
              
              {/* Routes de transport */}
              {showRoutes && (
                <>
                  {(selectedPort === 'nouakchott' || selectedPort === 'mixed') && (
                    <Polyline
                      positions={routes.nouakchott_to_riah2.path}
                      color={ports.nouakchott.color}
                      weight={4}
                      opacity={0.7}
                    >
                      <LeafletTooltip>
                        Route Nouakchott → RiaH2<br/>
                        Distance: {routes.nouakchott_to_riah2.distance} km<br/>
                        Type: {routes.nouakchott_to_riah2.roadType}<br/>
                        Risque: {routes.nouakchott_to_riah2.risk}/100
                      </LeafletTooltip>
                    </Polyline>
                  )}
                  
                  {(selectedPort === 'nouadhibou' || selectedPort === 'mixed') && (
                    <Polyline
                      positions={routes.nouadhibou_to_riah2.path}
                      color={ports.nouadhibou.color}
                      weight={4}
                      opacity={0.7}
                    >
                      <LeafletTooltip>
                        Route Nouadhibou → RiaH2<br/>
                        Distance: {routes.nouadhibou_to_riah2.distance} km<br/>
                        Type: {routes.nouadhibou_to_riah2.roadType}<br/>
                        Risque: {routes.nouadhibou_to_riah2.risk}/100
                      </LeafletTooltip>
                    </Polyline>
                  )}
                </>
              )}
            </MapContainer>
          </div>

          {/* Légende de la carte */}
          <div className="mt-4 bg-gray-50 p-4 rounded-lg">
            <h4 className="font-semibold text-gray-700 mb-2">Légende</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div className="flex items-center">
                <div className="w-4 h-4 bg-blue-500 rounded-full mr-2"></div>
                <span>Port de Nouakchott</span>
              </div>
              <div className="flex items-center">
                <div className="w-4 h-4 bg-green-500 rounded-full mr-2"></div>
                <span>Port de Nouadhibou</span>
              </div>
              <div className="flex items-center">
                <div className="w-4 h-4 bg-purple-500 rounded-full mr-2"></div>
                <span>Site RiaH2</span>
              </div>
              <div className="flex items-center">
                <div className="w-4 h-4 bg-red-500 rounded-full mr-2"></div>
                <span>Capitale</span>
              </div>
              <div className="flex items-center">
                <div className="w-4 h-4 bg-yellow-500 rounded-full mr-2"></div>
                <span>Ville</span>
              </div>
              <div className="flex items-center">
                <div className="w-4 h-4 bg-green-400 rounded-full mr-2"></div>
                <span>Ravitaillement</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 border-2 border-red-500 rounded-full mr-2"></div>
                <span>Zone à risque</span>
              </div>
              <div className="flex items-center">
                <div className="w-8 h-0.5 bg-gray-400 mr-2" style={{borderTop: '2px dashed #94a3b8'}}></div>
                <span>Routes nationales</span>
              </div>
            </div>
          </div>
        </div>

        {/* Résultats d'optimisation */}
        {optimizationResults && (
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
              <TrendingUp className="mr-2 text-green-600" />
              Résultats de l'Optimisation
            </h3>
            
            {/* Score pondéré */}
            <div className="mb-6 bg-gradient-to-r from-blue-50 to-green-50 p-4 rounded-lg border-2 border-blue-200">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-semibold text-gray-700">Score d'Optimisation Pondéré</h4>
                  <p className="text-sm text-gray-600">Basé sur vos critères de pondération</p>
                </div>
                <div className="text-4xl font-bold text-blue-600">
                  {optimizationResults.weightedScore}/100
                </div>
              </div>
            </div>

            {/* Allocation des composants */}
            <div className="mb-6">
              <h4 className="font-semibold text-gray-700 mb-3">Allocation des Composants</h4>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Composant</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nouakchott</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nouadhibou</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Quantité Totale</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Éoliennes 6 MW</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {optimizationResults.allocation.eoliennes.nouakchott}% ({Math.round(projectComponents.eoliennes.quantity * optimizationResults.allocation.eoliennes.nouakchott / 100)})
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {optimizationResults.allocation.eoliennes.nouadhibou}% ({Math.round(projectComponents.eoliennes.quantity * optimizationResults.allocation.eoliennes.nouadhibou / 100)})
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{projectComponents.eoliennes.quantity}</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Panneaux Solaires</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {optimizationResults.allocation.panneaux.nouakchott}% ({(projectComponents.panneaux.quantity * optimizationResults.allocation.panneaux.nouakchott / 100 / 1000000).toFixed(1)}M)
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {optimizationResults.allocation.panneaux.nouadhibou}% ({(projectComponents.panneaux.quantity * optimizationResults.allocation.panneaux.nouadhibou / 100 / 1000000).toFixed(1)}M)
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{(projectComponents.panneaux.quantity / 1000000).toFixed(0)}M</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">Électrolyseurs 10 MW</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {optimizationResults.allocation.electrolyseurs.nouakchott}% ({Math.round(projectComponents.electrolyseurs.quantity * optimizationResults.allocation.electrolyseurs.nouakchott / 100)})
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {optimizationResults.allocation.electrolyseurs.nouadhibou}% ({Math.round(projectComponents.electrolyseurs.quantity * optimizationResults.allocation.electrolyseurs.nouadhibou / 100)})
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{projectComponents.electrolyseurs.quantity}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Métriques clés */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="text-sm text-gray-600 mb-1">Coût Total</div>
                <div className="text-2xl font-bold text-blue-600">{optimizationResults.totalCost} M€</div>
              </div>
              <div className="bg-green-50 p-4 rounded-lg">
                <div className="text-sm text-gray-600 mb-1">Délai Moyen</div>
                <div className="text-2xl font-bold text-green-600">{optimizationResults.avgDelay} jours</div>
              </div>
              <div className="bg-yellow-50 p-4 rounded-lg">
                <div className="text-sm text-gray-600 mb-1">Risque Global</div>
                <div className="text-2xl font-bold text-yellow-600">{optimizationResults.totalRisk}/100</div>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg">
                <div className="text-sm text-gray-600 mb-1">Émissions CO₂</div>
                <div className="text-2xl font-bold text-purple-600">{optimizationResults.totalCO2} kt</div>
              </div>
              <div className="bg-indigo-50 p-4 rounded-lg">
                <div className="text-sm text-gray-600 mb-1">Score Pondéré</div>
                <div className="text-2xl font-bold text-indigo-600">{optimizationResults.weightedScore}</div>
              </div>
            </div>

            {/* Calculateur de Coûts Détaillé */}
            <div className="border-t pt-6">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-semibold text-gray-700 flex items-center">
                  <DollarSign className="mr-2 text-green-600" />
                  Décomposition Détaillée des Coûts
                </h4>
                <button
                  onClick={() => setShowCostDetails(!showCostDetails)}
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                >
                  {showCostDetails ? 'Masquer' : 'Afficher'}
                </button>
              </div>

              {showCostDetails && optimizationResults.costDetails && (
                <div className="space-y-6">
                  {/* Graphique de décomposition */}
                  <div>
                    <h5 className="text-sm font-semibold text-gray-600 mb-3">Répartition par Poste de Coût</h5>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={costBreakdownData}>
                        <XAxis dataKey="category" angle={-45} textAnchor="end" height={100} fontSize={12} />
                        <YAxis label={{ value: 'Coût (M€)', angle: -90, position: 'insideLeft' }} />
                        <Tooltip />
                        <Bar dataKey="value" fill="#3b82f6" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Tableaux détaillés */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Coûts portuaires */}
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <h5 className="font-semibold text-blue-800 mb-3">Coûts Portuaires</h5>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-700">Déchargement:</span>
                          <span className="font-semibold">{optimizationResults.costDetails.portCosts.unloading} M€</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-700">Stockage:</span>
                          <span className="font-semibold">{optimizationResults.costDetails.portCosts.storage} M€</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-700">Manutention:</span>
                          <span className="font-semibold">{optimizationResults.costDetails.portCosts.handling} M€</span>
                        </div>
                        <div className="flex justify-between border-t pt-2 mt-2">
                          <span className="text-gray-800 font-semibold">Total:</span>
                          <span className="font-bold text-blue-600">{optimizationResults.costDetails.portCosts.total} M€</span>
                        </div>
                      </div>
                    </div>

                    {/* Coûts de transport */}
                    <div className="bg-green-50 p-4 rounded-lg">
                      <h5 className="font-semibold text-green-800 mb-3">Coûts de Transport</h5>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-700">Standard:</span>
                          <span className="font-semibold">{optimizationResults.costDetails.transportCosts.standard} M€</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-700">Exceptionnel:</span>
                          <span className="font-semibold">{optimizationResults.costDetails.transportCosts.exceptional} M€</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-700">Escorte:</span>
                          <span className="font-semibold">{optimizationResults.costDetails.transportCosts.escort} M€</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-700">Assurance:</span>
                          <span className="font-semibold">{optimizationResults.costDetails.transportCosts.insurance} M€</span>
                        </div>
                        <div className="flex justify-between border-t pt-2 mt-2">
                          <span className="text-gray-800 font-semibold">Total:</span>
                          <span className="font-bold text-green-600">{optimizationResults.costDetails.transportCosts.total} M€</span>
                        </div>
                      </div>
                    </div>

                    {/* Coûts indirects */}
                    <div className="bg-purple-50 p-4 rounded-lg">
                      <h5 className="font-semibold text-purple-800 mb-3">Coûts Indirects</h5>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-700">Immobilisation:</span>
                          <span className="font-semibold">{optimizationResults.costDetails.indirectCosts.capitalImmobilization} M€</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-700">Provision dommages:</span>
                          <span className="font-semibold">{optimizationResults.costDetails.indirectCosts.damageProvision} M€</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-700">Administratif:</span>
                          <span className="font-semibold">{optimizationResults.costDetails.indirectCosts.administrative} M€</span>
                        </div>
                        <div className="flex justify-between border-t pt-2 mt-2">
                          <span className="text-gray-800 font-semibold">Total:</span>
                          <span className="font-bold text-purple-600">{optimizationResults.costDetails.indirectCosts.total} M€</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Coût total */}
                  <div className="bg-gradient-to-r from-gray-100 to-gray-200 p-4 rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-bold text-gray-800">COÛT TOTAL LOGISTIQUE</span>
                      <span className="text-3xl font-bold text-gray-900">{optimizationResults.costDetails.total} M€</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Comparaison des scénarios */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-xl font-bold text-gray-800 mb-4">Comparaison des Scénarios</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold text-gray-700 mb-3 text-center">Coûts et Délais</h4>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={comparisonData}>
                  <XAxis dataKey="scenario" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="totalCost" fill="#3b82f6" name="Coût (M€)" />
                  <Bar dataKey="avgDelay" fill="#10b981" name="Délai (jours)" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div>
              <h4 className="font-semibold text-gray-700 mb-3 text-center">Analyse Multi-Critères</h4>
              <ResponsiveContainer width="100%" height={250}>
                <RadarChart data={comparisonData}>
                  <PolarGrid />
                  <PolarAngleAxis dataKey="scenario" />
                  <PolarRadiusAxis />
                  <Radar name="Coût" dataKey="totalCost" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.3} />
                  <Radar name="Risque" dataKey="totalRisk" stroke="#ef4444" fill="#ef4444" fillOpacity={0.3} />
                  <Tooltip />
                  <Legend />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Tableau comparatif avec scores pondérés */}
          <div className="mt-6 overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Scénario</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Coût (M€)</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Délai (jours)</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Risque</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">CO₂ (kt)</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Score Pondéré</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {comparisonData.map((data, idx) => (
                  <tr key={idx} className={selectedPort === ['nouakchott', 'nouadhibou', 'mixed'][idx] ? 'bg-blue-50' : ''}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{data.scenario}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{data.totalCost}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{data.avgDelay}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{data.totalRisk}/100</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{data.totalCO2}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-indigo-600">{data.weightedScore}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Recommandations */}
        <div className="bg-green-50 border-l-4 border-green-400 p-4">
          <h4 className="font-semibold text-green-800 flex items-center">
            <CheckCircle className="mr-2" size={20} />
            Recommandation
          </h4>
          <p className="text-green-700 text-sm mt-2">
            La <strong>stratégie mixte</strong> est recommandée pour le projet RiaH2. Elle permet d'économiser <strong>17 M€ (7%)</strong> par rapport à un port unique 
            et réduit le délai moyen de livraison de <strong>2 à 6 jours</strong>. Les électrolyseurs (80t) transitent exclusivement par Nouadhibou grâce à sa capacité 
            de levage supérieure, tandis que les panneaux solaires privilégient Nouakchott pour sa proximité avec le site. Le score pondéré de <strong>{comparisonData[2].weightedScore}/100</strong> confirme 
            cette stratégie comme optimale selon vos critères.
          </p>
        </div>

        {/* Informations sur les infrastructures */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white p-6 rounded-lg shadow">
            <h4 className="font-semibold text-blue-600 mb-3 flex items-center">
              <Anchor className="mr-2" />
              Port de Nouakchott
            </h4>
            <ul className="space-y-2 text-sm text-gray-700">
              <li><strong>Localisation:</strong> 18°06'N, 15°57'W</li>
              <li><strong>Capacité levage:</strong> 40 tonnes max</li>
              <li><strong>Tirant d'eau:</strong> 8-10 mètres</li>
              <li><strong>Coût déchargement:</strong> {ports.nouakchott.cost}€/t</li>
              <li><strong>Coût stockage:</strong> {ports.nouakchott.storageCost}€/m²/jour</li>
              <li><strong>Avantages:</strong> Proximité capitale, infrastructure routière vers l'est</li>
              <li><strong>Inconvénients:</strong> Capacité limitée, congestion portuaire</li>
              <li><strong>Distance vers RiaH2:</strong> 350 km (route asphaltée)</li>
            </ul>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <h4 className="font-semibold text-green-600 mb-3 flex items-center">
              <Anchor className="mr-2" />
              Port de Nouadhibou
            </h4>
            <ul className="space-y-2 text-sm text-gray-700">
              <li><strong>Localisation:</strong> 20°54'N, 17°03'W</li>
              <li><strong>Capacité levage:</strong> 100+ tonnes</li>
              <li><strong>Tirant d'eau:</strong> 12-15 mètres</li>
              <li><strong>Coût déchargement:</strong> {ports.nouadhibou.cost}€/t</li>
              <li><strong>Coût stockage:</strong> {ports.nouadhibou.storageCost}€/m²/jour</li>
              <li><strong>Avantages:</strong> Infrastructure lourde, moins de congestion</li>
              <li><strong>Inconvénients:</strong> Plus éloigné, routes moins développées</li>
              <li><strong>Distance vers RiaH2:</strong> 520 km (route mixte)</li>
            </ul>
          </div>
        </div>
      </div>
    );
  };

  // Les autres composants d'onglets restent inchangés...
  // (OverviewTab, CalculatorTab, SimulationTab, ScenariosTab)
  
  const OverviewTab = () => {
    const projectStats = [
      { label: "Capacité Énergétique", value: "20 GW", detail: "12 GW éolien + 8 GW solaire", icon: TrendingUp },
      { label: "Capacité Électrolyse", value: "10 GW", detail: "1000 modules de 10 MW", icon: Target },
      { label: "Production H₂", value: "1.2 Mt/an", detail: "Hydrogène vert", icon: CheckCircle },
      { label: "Investissement", value: "30 Md USD", detail: "Sur 8 ans", icon: Calculator }
    ];

    const componentData = [
      { name: 'Éoliennes', value: 480000, color: '#3b82f6' },
      { name: 'Panneaux', value: 500000, color: '#10b981' },
      { name: 'Électrolyseurs', value: 80000, color: '#f59e0b' }
    ];

    return (
      <div className="space-y-6">
        <div className="bg-gradient-to-r from-blue-600 to-green-600 text-white p-6 rounded-lg shadow-lg">
          <h2 className="text-3xl font-bold mb-2">Projet RiaH2 - Mauritanie</h2>
          <p className="text-blue-100">
            Méga-projet d'hydrogène vert combinant éolien, solaire et électrolyse à grande échelle
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {projectStats.map((stat, index) => (
            <div key={index} className="bg-white p-6 rounded-lg shadow hover:shadow-lg transition-shadow">
              <div className="flex items-center justify-between mb-2">
                <stat.icon className="text-blue-600" size={24} />
              </div>
              <div className="text-2xl font-bold text-gray-800">{stat.value}</div>
              <div className="text-sm text-gray-600">{stat.label}</div>
              <div className="text-xs text-gray-500 mt-1">{stat.detail}</div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Répartition des Poids (tonnes)</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={componentData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {componentData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Composants Clés</h3>
            <div className="space-y-4">
              <div className="border-l-4 border-blue-500 pl-4">
                <h4 className="font-semibold text-gray-800">Éoliennes 6 MW</h4>
                <p className="text-sm text-gray-600">2000 unités • 240 tonnes/unité</p>
                <p className="text-xs text-gray-500">Dimensions: 75m × 5m × 5m</p>
              </div>
              <div className="border-l-4 border-green-500 pl-4">
                <h4 className="font-semibold text-gray-800">Panneaux Solaires 0.6 kW</h4>
                <p className="text-sm text-gray-600">13.3 millions d'unités • 37.5 kg/unité</p>
                <p className="text-xs text-gray-500">Dimensions: 2m × 1m × 0.05m</p>
              </div>
              <div className="border-l-4 border-yellow-500 pl-4">
                <h4 className="font-semibold text-gray-800">Électrolyseurs 10 MW</h4>
                <p className="text-sm text-gray-600">1000 modules • 80 tonnes/module</p>
                <p className="text-xs text-gray-500">Dimensions: 12m × 3m × 4m</p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
          <h4 className="font-semibold text-blue-800">🎯 Objectif Pédagogique</h4>
          <p className="text-blue-700 text-sm mt-1">
            Comprendre les défis logistiques spécifiques aux méga-projets d'hydrogène vert en Mauritanie : 
            transport de composants massifs, coordination multi-technologies, et optimisation des flux dans un environnement désertique.
          </p>
        </div>
      </div>
    );
  };

  const CalculatorTab = () => {
    const [windTurbines, setWindTurbines] = useState(2000);
    const [solarPanels, setSolarPanels] = useState(20000000);
    const [electrolyzers, setElectrolyzers] = useState(1000);
    const [distance, setDistance] = useState(400);
    const [turbineSize, setTurbineSize] = useState(6);
    const [pvPanelSize, setPvPanelSize] = useState(0.4);
    const [electrolyzerSize, setElectrolyzerSize] = useState(10);

    const calculateLogistics = () => {
      const turbineWeight = turbineSize * 40;
      const pvPanelWeight = pvPanelSize * 62.5;
      const electrolyzerWeight = electrolyzerSize * 8;

      const totalWeightTurbines = windTurbines * turbineWeight;
      const totalWeightPanels = solarPanels * (pvPanelWeight / 1000);
      const totalWeightElectrolyzers = electrolyzers * electrolyzerWeight;
      const totalWeight = totalWeightTurbines + totalWeightPanels + totalWeightElectrolyzers;

      const tripsNeeded = Math.ceil(totalWeight / 40);
      const transportCost = (totalWeight * distance * 2.5) / 1000000;
      const portHandling = totalWeight * 15 / 1000000;
      const totalCost = transportCost + portHandling;
      const timeEstimate = Math.ceil(tripsNeeded / 5);

      return {
        totalWeight: totalWeight.toFixed(0),
        tripsNeeded,
        transportCost: transportCost.toFixed(1),
        portHandling: portHandling.toFixed(1),
        totalCost: totalCost.toFixed(1),
        timeEstimate
      };
    };

    const results = calculateLogistics();

    return (
      <div className="space-y-6">
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Calculateur Logistique</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre d'éoliennes
                </label>
                <input
                  type="number"
                  value={windTurbines}
                  onChange={(e) => setWindTurbines(parseInt(e.target.value) || 0)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Taille éolienne (MW)
                </label>
                <input
                  type="number"
                  value={turbineSize}
                  onChange={(e) => setTurbineSize(parseFloat(e.target.value) || 0)}
                  step="0.5"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre de panneaux solaires
                </label>
                <input
                  type="number"
                  value={solarPanels}
                  onChange={(e) => setSolarPanels(parseInt(e.target.value) || 0)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Taille panneau PV (kW)
                </label>
                <input
                  type="number"
                  value={pvPanelSize}
                  onChange={(e) => setPvPanelSize(parseFloat(e.target.value) || 0)}
                  step="0.1"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre d'électrolyseurs
                </label>
                <input
                  type="number"
                  value={electrolyzers}
                  onChange={(e) => setElectrolyzers(parseInt(e.target.value) || 0)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Taille électrolyseur (MW)
                </label>
                <input
                  type="number"
                  value={electrolyzerSize}
                  onChange={(e) => setElectrolyzerSize(parseFloat(e.target.value) || 0)}
                  step="1"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Distance port-site (km)
                </label>
                <input
                  type="number"
                  value={distance}
                  onChange={(e) => setDistance(parseInt(e.target.value) || 0)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-xl font-bold text-gray-800 mb-4">Résultats</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="text-sm text-gray-600 mb-1">Poids Total</div>
              <div className="text-2xl font-bold text-blue-600">{results.totalWeight} t</div>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="text-sm text-gray-600 mb-1">Voyages Nécessaires</div>
              <div className="text-2xl font-bold text-green-600">{results.tripsNeeded}</div>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg">
              <div className="text-sm text-gray-600 mb-1">Coût Transport</div>
              <div className="text-2xl font-bold text-purple-600">{results.transportCost} M€</div>
            </div>
            <div className="bg-yellow-50 p-4 rounded-lg">
              <div className="text-sm text-gray-600 mb-1">Manutention Port</div>
              <div className="text-2xl font-bold text-yellow-600">{results.portHandling} M€</div>
            </div>
            <div className="bg-red-50 p-4 rounded-lg">
              <div className="text-sm text-gray-600 mb-1">Coût Total</div>
              <div className="text-2xl font-bold text-red-600">{results.totalCost} M€</div>
            </div>
            <div className="bg-indigo-50 p-4 rounded-lg">
              <div className="text-sm text-gray-600 mb-1">Durée Estimée</div>
              <div className="text-2xl font-bold text-indigo-600">{results.timeEstimate} mois</div>
            </div>
          </div>
        </div>

        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
          <h4 className="font-semibold text-yellow-800">💡 Note</h4>
          <p className="text-yellow-700 text-sm mt-1">
            Ces calculs sont des estimations simplifiées. Les coûts réels dépendent de nombreux facteurs : 
            état des routes, conditions météo, disponibilité des équipements, négociations avec les transporteurs, etc.
          </p>
        </div>
      </div>
    );
  };

  const SimulationTab = () => {
    return (
      <div className="space-y-6">
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-gray-800">Simulation par Phases</h2>
            <div className="flex space-x-2">
              <button
                onClick={() => {
                  setIsSimulationRunning(!isSimulationRunning);
                }}
                className={`flex items-center px-4 py-2 rounded-lg font-medium ${
                  isSimulationRunning 
                    ? 'bg-yellow-500 hover:bg-yellow-600 text-white' 
                    : 'bg-green-500 hover:bg-green-600 text-white'
                }`}
              >
                {isSimulationRunning ? (
                  <><Pause size={18} className="mr-2" /> Pause</>
                ) : (
                  <><Play size={18} className="mr-2" /> Démarrer</>
                )}
              </button>
              <button
                onClick={() => {
                  setSimulationStep(0);
                  setIsSimulationRunning(false);
                  setKpiHistory([]);
                }}
                className="flex items-center px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg font-medium"
              >
                <RotateCcw size={18} className="mr-2" /> Réinitialiser
              </button>
            </div>
          </div>

          <div className="mb-6">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-gray-700">Progression</span>
              <span className="text-sm font-medium text-gray-700">
                Phase {simulationStep + 1} / {simulationData.length}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className="bg-blue-600 h-3 rounded-full transition-all duration-500"
                style={{ width: `${((simulationStep + 1) / simulationData.length) * 100}%` }}
              />
            </div>
          </div>

          <div className="bg-gradient-to-r from-blue-50 to-green-50 p-6 rounded-lg border-2 border-blue-200">
            <h3 className="text-xl font-bold text-gray-800 mb-2">
              {simulationData[simulationStep].phase}
            </h3>
            <p className="text-gray-600 mb-4">{simulationData[simulationStep].duration}</p>
            
            <div className="mb-4">
              <h4 className="font-semibold text-gray-700 mb-2">Défis Logistiques:</h4>
              <ul className="list-disc list-inside space-y-1">
                {simulationData[simulationStep].challenges.map((challenge, idx) => (
                  <li key={idx} className="text-gray-600">{challenge}</li>
                ))}
              </ul>
            </div>

            <div className="bg-white p-4 rounded-lg">
              <h4 className="font-semibold text-gray-700 mb-2">Composants installés:</h4>
              <p className="text-gray-600">{simulationData[simulationStep].components}</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white p-6 rounded-lg shadow">
            <h4 className="font-semibold text-gray-700 mb-2">Coût Cumulé</h4>
            <div className="text-3xl font-bold text-blue-600">
              {simulationData[simulationStep].kpis.cost} M€
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <h4 className="font-semibold text-gray-700 mb-2">Efficacité</h4>
            <div className="text-3xl font-bold text-green-600">
              {simulationData[simulationStep].kpis.efficiency}%
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <h4 className="font-semibold text-gray-700 mb-2">Niveau de Risque</h4>
            <div className="text-3xl font-bold text-red-600">
              {simulationData[simulationStep].kpis.risk}%
            </div>
          </div>
        </div>

        {kpiHistory.length > 0 && (
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Évolution des KPIs</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={kpiHistory}>
                <XAxis dataKey="step" label={{ value: 'Phase', position: 'insideBottom', offset: -5 }} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="cost" stroke="#3b82f6" name="Coût (M€)" strokeWidth={2} />
                <Line type="monotone" dataKey="efficiency" stroke="#10b981" name="Efficacité (%)" strokeWidth={2} />
                <Line type="monotone" dataKey="risk" stroke="#ef4444" name="Risque (%)" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    );
  };

  const ScenariosTab = () => {
    const handleChoice = (choice) => {
      const newScore = score + (choice.impact.cost < 0 ? 10 : choice.impact.delay < 2 ? 5 : 0);
      setScore(newScore);
      setScenarioResult({ choice });
    };

    return (
      <div className="space-y-6">
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-gray-800">Scénarios de Crise</h2>
            <div className="bg-blue-100 px-4 py-2 rounded-lg">
              <span className="text-sm font-medium text-blue-800">Score: {score} points</span>
            </div>
          </div>
          <p className="text-gray-600">
            Testez votre capacité à gérer des crises logistiques complexes dans un projet hybride éolien-solaire-H₂.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {scenarios.map(scenario => (
            <button
              key={scenario.id}
              onClick={() => {
                setCurrentScenario(scenario);
                setScenarioResult(null);
              }}
              disabled={currentScenario !== null}
              className={`bg-white p-6 rounded-lg shadow hover:shadow-lg transition-shadow text-left ${
                currentScenario !== null ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              <div className="flex items-start mb-3">
                <AlertTriangle className="text-red-500 mr-2 flex-shrink-0" size={24} />
                <h3 className="font-bold text-gray-800">{scenario.title}</h3>
              </div>
              <p className="text-sm text-gray-600">{scenario.description}</p>
            </button>
          ))}
        </div>

        {currentScenario && (
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-start mb-4">
              <AlertTriangle className="text-red-500 mr-3 flex-shrink-0" size={32} />
              <div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">{currentScenario.title}</h3>
                <p className="text-gray-600">{currentScenario.description}</p>
              </div>
            </div>

            {!scenarioResult ? (
              <div className="space-y-3">
                <h4 className="font-semibold text-gray-700">Choisissez votre stratégie:</h4>
                {currentScenario.choices.map((choice, index) => (
                  <button
                    key={index}
                    onClick={() => handleChoice(choice)}
                    className="w-full text-left p-4 bg-gray-50 hover:bg-blue-50 rounded-lg border-2 border-gray-200 hover:border-blue-400 transition-all"
                  >
                    <div className="font-medium text-gray-800 mb-2">{choice.text}</div>
                    <div className="flex space-x-4 text-sm">
                      <span className={choice.impact.cost > 0 ? 'text-red-600' : 'text-green-600'}>
                        Coût: {choice.impact.cost > 0 ? '+' : ''}{choice.impact.cost}M€
                      </span>
                      <span className={choice.impact.delay > 0 ? 'text-red-600' : 'text-green-600'}>
                        Délai: {choice.impact.delay > 0 ? '+' : ''}{choice.impact.delay} mois
                      </span>
                      <span className={choice.impact.risk > 0 ? 'text-red-600' : 'text-green-600'}>
                        Risque: {choice.impact.risk > 0 ? '+' : ''}{choice.impact.risk}%
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-green-50 border-2 border-green-400 p-4 rounded-lg">
                  <h5 className="font-semibold text-green-800 mb-2">
                    ✅ Décision prise: {scenarioResult.choice.text}
                  </h5>
                  
                  <div className="grid grid-cols-3 gap-4 mt-3">
                    <div className="text-center">
                      <div className={`text-lg font-bold ${
                        scenarioResult.choice.impact.cost > 0 ? 'text-red-600' : 'text-green-600'
                      }`}>
                        {scenarioResult.choice.impact.cost > 0 ? '+' : ''}{scenarioResult.choice.impact.cost}M€
                      </div>
                      <div className="text-xs text-gray-600">Impact coût</div>
                    </div>
                    <div className="text-center">
                      <div className={`text-lg font-bold ${
                        scenarioResult.choice.impact.delay > 0 ? 'text-red-600' : 'text-green-600'
                      }`}>
                        {scenarioResult.choice.impact.delay > 0 ? '+' : ''}{scenarioResult.choice.impact.delay} mois
                      </div>
                      <div className="text-xs text-gray-600">Impact délai</div>
                    </div>
                    <div className="text-center">
                      <div className={`text-lg font-bold ${
                        scenarioResult.choice.impact.risk > 0 ? 'text-red-600' : 'text-green-600'
                      }`}>
                        {scenarioResult.choice.impact.risk > 0 ? '+' : ''}{scenarioResult.choice.impact.risk}%
                      </div>
                      <div className="text-xs text-gray-600">Impact risque</div>
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => {
                    setCurrentScenario(null);
                    setScenarioResult(null);
                  }}
                  className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700"
                >
                  Scénario suivant
                </button>
              </div>
            )}
          </div>
        )}

        <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
          <h4 className="font-semibold text-blue-800">📚 Analyse post-crise</h4>
          <p className="text-blue-700 text-sm mt-1">
            Projets hybrides = risques démultipliés : défaillance d'une chaîne (éolien/solaire/H2) impacte l'ensemble. 
            La diversification des fournisseurs et la modularité des installations sont cruciales pour la résilience.
          </p>
        </div>
      </div>
    );
  };

  const renderTabContent = () => {
    switch(activeTab) {
      case 'overview': return <OverviewTab />;
      case 'calculator': return <CalculatorTab />;
      case 'simulation': return <SimulationTab />;
      case 'scenarios': return <ScenariosTab />;
      case 'transport': return <TransportOptimizationTab />;
      default: return <OverviewTab />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Logistique des Méga-Projets Énergétiques
              </h1>
              <p className="text-gray-600">Module pédagogique - École d'Ingénieurs</p>
            </div>
            <div className="flex items-center space-x-2 text-sm">
              <Users className="text-gray-400" size={16} />
              <span className="text-gray-600">Projet RiaH2 - Mauritanie</span>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-gray-200 shadow-inner">
        <div className="max-w-7xl mx-auto px-4">
          <nav className="flex space-x-4">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center px-3 py-2 font-medium text-sm rounded-t-lg transition-all duration-300 ease-in-out 
                  ${activeTab === tab.id ? 'bg-white text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
              >
                <tab.icon className="mr-2" size={18} />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8 transition-opacity duration-500 ease-in-out">
        {renderTabContent()}
      </main>
    </div>
  );
};

export default LogisticsLearningApp;

